/**
 * Filter Storage — localStorage persistence for search/filter state & saved presets.
 *
 * Separate key from preferences.ts so the two domains stay independent.
 * Pattern mirrors utils/preferences.ts (versioning, quota handling, merge).
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface SavedFilterPreset {
  id: string;
  name: string;
  state: Record<string, unknown>;
  createdAt: number;
}

export interface FilterStore {
  version: 1;
  /** Last-applied filter state keyed by scope (e.g. "docs.proj_abc") */
  lastFilters: Record<string, { state: Record<string, unknown>; savedAt: number }>;
  /** Named presets keyed by scope */
  presets: Record<string, SavedFilterPreset[]>;
}

// ── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'martha.filters';
const CURRENT_VERSION = 1;
const STALE_DAYS = 30;
const STALE_MS = STALE_DAYS * 24 * 60 * 60 * 1000;

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultStore(): FilterStore {
  return { version: 1, lastFilters: {}, presets: {} };
}

/** Remove lastFilters entries older than 30 days */
function evictStale(store: FilterStore): FilterStore {
  const cutoff = Date.now() - STALE_MS;
  const lastFilters = { ...store.lastFilters };
  for (const key of Object.keys(lastFilters)) {
    if (lastFilters[key].savedAt < cutoff) {
      delete lastFilters[key];
    }
  }
  return { ...store, lastFilters };
}

// ── Public API ───────────────────────────────────────────────────────────────

export function loadFilterStore(): FilterStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultStore();

    const parsed = JSON.parse(raw) as FilterStore;
    if (!parsed || typeof parsed !== 'object' || parsed.version !== CURRENT_VERSION) {
      // Unknown version — reset rather than corrupt
      localStorage.removeItem(STORAGE_KEY);
      return getDefaultStore();
    }

    return evictStale({
      version: CURRENT_VERSION,
      lastFilters: parsed.lastFilters || {},
      presets: parsed.presets || {},
    });
  } catch {
    console.error('Failed to parse filter store — resetting');
    localStorage.removeItem(STORAGE_KEY);
    return getDefaultStore();
  }
}

export function saveFilterStore(store: FilterStore): void {
  try {
    const serialized = JSON.stringify(store);
    if (serialized.length > 500_000) {
      console.warn('Filter store is large:', serialized.length, 'bytes');
    }
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch (e) {
    if (e instanceof Error && e.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded — clearing filter store');
      // Keep presets, drop lastFilters
      const minimal: FilterStore = { version: 1, lastFilters: {}, presets: store.presets };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(minimal));
    } else {
      console.error('Failed to save filter store:', e);
    }
  }
}
